import { useEffect } from 'react';
import { Stack, Divider, Text, Box } from '@chakra-ui/react';
import { Field } from 'formik';
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Select as NativeSelect,
} from '@/components/v4';
import { Select } from 'chakra-react-select';
import { defaultChakraSelectStyle, chakraSelectDarkMode } from '@/constants';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';

const ACCOUNT_TYPE_OPTIONS = [
    { value: 'User', label: 'User' },
    { value: 'RingGroup', label: 'Ring Group' },
];

const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#757575',
    paddingTop: '8px',
    paddingBottom: '4px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
};

export default function CreateUserStep({
    values,
    setFieldValue,
    errors,
    touched,
    darkMode,
    siteOptions = [],
}) {
    const chakraStyles = {
        ...defaultChakraSelectStyle,
        ...(darkMode ? chakraSelectDarkMode(darkMode) : {}),
    };

    const isRingGroup = values.accountType === 'RingGroup';
    const isSingleSite = siteOptions.length === 1;
    const singleSiteValue = siteOptions[0]?.value;

    // When there's only one site available, auto-select it and render it as
    // a read-only value instead of a dropdown.
    useEffect(() => {
        if (isSingleSite && values.siteId !== singleSiteValue) {
            setFieldValue('siteId', singleSiteValue);
        }
    }, [isSingleSite, singleSiteValue, values.siteId, setFieldValue]);

    return (
        <>
            <Text sx={sectionTitleStyle}>Basic Information</Text>
            <Divider mb={1} />

            {/* Account Type */}
            <FormControl
                sx={{ margin: '0 !important' }}
                isInvalid={!!errors.accountType && touched.accountType}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Account Type <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        <NativeSelect
                            options={ACCOUNT_TYPE_OPTIONS}
                            value={values.accountType}
                            onChange={(e) =>
                                setFieldValue('accountType', e.target.value)
                            }
                        />
                        {errors.accountType && touched.accountType && (
                            <FormErrorMessage>
                                {errors.accountType}
                            </FormErrorMessage>
                        )}
                    </Box>
                </Stack>
            </FormControl>

            {isRingGroup ? (
                /* Name (Ring Group display name) */
                <FormControl
                    sx={{
                        margin: '0 !important',
                        marginTop: '10px !important',
                    }}
                    isInvalid={!!errors.name && touched.name}>
                    <Stack
                        direction="row"
                        spacing={stackSpacing}
                        sx={stackStyling()}>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            Name <span style={{ color: '#e53e3e' }}>*</span>
                        </FormLabel>
                        <Box flex="1" pt="15px">
                            <Field name="name">
                                {({ field }) => (
                                    <Input {...field} placeholder="Name" />
                                )}
                            </Field>
                            {errors.name && touched.name && (
                                <FormErrorMessage>
                                    {errors.name}
                                </FormErrorMessage>
                            )}
                        </Box>
                    </Stack>
                </FormControl>
            ) : (
                <>
                    {/* First Name */}
                    <FormControl
                        sx={{
                            margin: '0 !important',
                            marginTop: '10px !important',
                        }}
                        isInvalid={!!errors.firstName && touched.firstName}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling()}>
                            <FormLabel sx={formLabelStyling(darkMode)}>
                                First Name{' '}
                                <span style={{ color: '#e53e3e' }}>*</span>
                            </FormLabel>
                            <Box flex="1" pt="15px">
                                <Field name="firstName">
                                    {({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="First Name"
                                        />
                                    )}
                                </Field>
                                {errors.firstName && touched.firstName && (
                                    <FormErrorMessage>
                                        {errors.firstName}
                                    </FormErrorMessage>
                                )}
                            </Box>
                        </Stack>
                    </FormControl>

                    {/* Last Name */}
                    <FormControl
                        sx={{
                            margin: '0 !important',
                            marginTop: '10px !important',
                        }}
                        isInvalid={!!errors.lastName && touched.lastName}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling()}>
                            <FormLabel sx={formLabelStyling(darkMode)}>
                                Last Name{' '}
                                <span style={{ color: '#e53e3e' }}>*</span>
                            </FormLabel>
                            <Box flex="1" pt="15px">
                                <Field name="lastName">
                                    {({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Last Name"
                                        />
                                    )}
                                </Field>
                                {errors.lastName && touched.lastName && (
                                    <FormErrorMessage>
                                        {errors.lastName}
                                    </FormErrorMessage>
                                )}
                            </Box>
                        </Stack>
                    </FormControl>
                </>
            )}

            {/* Email — optional for every account type */}
            <FormControl
                sx={{
                    margin: '0 !important',
                    marginTop: '10px !important',
                }}
                isInvalid={!!errors.email && touched.email}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>Email</FormLabel>
                    <Box flex="1" pt="15px">
                        <Field name="email">
                            {({ field }) => (
                                <Input
                                    {...field}
                                    type="email"
                                    placeholder="email@example.com"
                                />
                            )}
                        </Field>
                        {errors.email && touched.email && (
                            <FormErrorMessage>{errors.email}</FormErrorMessage>
                        )}
                    </Box>
                </Stack>
            </FormControl>

            {/* Site — a read-only value when only one site is available */}
            <FormControl
                sx={{
                    margin: '0 !important',
                    marginTop: '10px !important',
                }}
                isInvalid={!!errors.siteId && touched.siteId}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Site <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        {isSingleSite ? (
                            <Input
                                isReadOnly
                                isDisabled
                                value={siteOptions[0].label}
                            />
                        ) : (
                            <Select
                                value={
                                    siteOptions.find(
                                        (o) => o.value === values.siteId,
                                    ) || null
                                }
                                options={siteOptions}
                                onChange={(opt) =>
                                    setFieldValue('siteId', opt?.value || '')
                                }
                                placeholder="Select a site"
                                chakraStyles={chakraStyles}
                                isInvalid={!!errors.siteId && touched.siteId}
                            />
                        )}
                        {errors.siteId && touched.siteId && (
                            <FormErrorMessage>{errors.siteId}</FormErrorMessage>
                        )}
                    </Box>
                </Stack>
            </FormControl>

            {isRingGroup && (
                /* Platform Extension */
                <FormControl
                    sx={{
                        margin: '0 !important',
                        marginTop: '10px !important',
                    }}
                    isInvalid={
                        !!errors.platformExtension && touched.platformExtension
                    }>
                    <Stack
                        direction="row"
                        spacing={stackSpacing}
                        sx={stackStyling()}>
                        <FormLabel sx={formLabelStyling(darkMode)}>
                            Platform Extension
                        </FormLabel>
                        <Box flex="1" pt="15px">
                            <Field name="platformExtension">
                                {({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Platform Extension"
                                    />
                                )}
                            </Field>
                            {errors.platformExtension &&
                                touched.platformExtension && (
                                    <FormErrorMessage>
                                        {errors.platformExtension}
                                    </FormErrorMessage>
                                )}
                        </Box>
                    </Stack>
                </FormControl>
            )}

            {/* Timezone */}
            {/* <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Timezone
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        <Select
                            value={
                                TIMEZONES.find(
                                    (o) => o.value === values.timezone,
                                ) || null
                            }
                            options={TIMEZONES}
                            onChange={(opt) =>
                                setFieldValue('timezone', opt?.value || '')
                            }
                            chakraStyles={chakraStyles}
                        />
                    </Box>
                </Stack>
            </FormControl> */}

            {/* Language */}
            {/* <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Language
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        <Select
                            value={
                                LANGUAGES.find(
                                    (o) => o.value === values.language,
                                ) || null
                            }
                            options={LANGUAGES}
                            onChange={(opt) =>
                                setFieldValue('language', opt?.value || '')
                            }
                            chakraStyles={chakraStyles}
                        />
                    </Box>
                </Stack>
            </FormControl> */}

            {/* Display in Directory */}
            {/* <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Display in Directory
                    </FormLabel>
                    <HStack align="center" pt="22px">
                        <Switch
                            isChecked={values.displayInDirectory}
                            onChange={(e) =>
                                setFieldValue(
                                    'displayInDirectory',
                                    e.target.checked,
                                )
                            }
                            colorScheme="blue"
                            size="lg"
                        />
                    </HStack>
                </Stack>
            </FormControl> */}

            {/* Additional Information */}
            {/* <Divider mt={1} mb={1} />
            <Text sx={sectionTitleStyle}>Additional Information</Text>
            <Divider mb={1} /> */}

            {/* Job Title */}
            {/* <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Job Title
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        <Field name="jobTitle">
                            {({ field }) => (
                                <Input {...field} placeholder="e.g. Manager" />
                            )}
                        </Field>
                    </Box>
                </Stack>
            </FormControl> */}

            {/* Department */}
            {/* <FormControl sx={{ margin: '0 !important' }}>
                <Stack
                    direction="row"
                    spacing={stackSpacing}
                    sx={stackStyling()}>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Department
                    </FormLabel>
                    <Box flex="1" pt="15px">
                        <Field name="department">
                            {({ field }) => (
                                <Input {...field} placeholder="e.g. Sales" />
                            )}
                        </Field>
                    </Box>
                </Stack>
            </FormControl> */}
        </>
    );
}
